import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../../components/table/DataTable';
import { dashboardKpis, workflowLoad, type WorkflowLoadRow } from '../../data/dashboards';
import { fltSelClass } from '../../utils/alSelClass';
import { translateDashboardWorkflowLabel } from '../../utils/translateKnownLabel';

const KPI_LABEL_KEYS = ['openTasks', 'slaAtRisk', 'overdue', 'avgStageDuration'] as const;
const KPI_HINT_KEYS = ['openTasksHint', 'slaAtRiskHint', 'overdueHint', 'avgStageDurationHint'] as const;

export default function DashboardsPage() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('30d');

  const loadColumns = useMemo<Column<WorkflowLoadRow>[]>(() => {
    return [
      { key: 'w', header: t('dashboards.workflowColumn'), getValue: (r) => translateDashboardWorkflowLabel(r.workflow) },
      { key: 'a', header: t('dashboards.activeTasksColumn'), getValue: (r) => String(r.active) },
      {
        key: 'o',
        header: t('dashboards.overdue'),
        getValue: (r) => String(r.overdue),
        render: (r) => (r.overdue > 0 ? <span className="ae">{r.overdue}</span> : <span style={{ color: 'var(--t3)' }}>0</span>),
      },
    ];
  }, [t]);

  const maxActive = useMemo(() => Math.max(1, ...workflowLoad.map((w) => w.active)), []);

  return (
    <>
      <div className="bc">
        <span>{t('dashboards.breadcrumb')}</span>
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('dashboards.title')}</div>
          {/* <div className="pg-sub">{t('dashboards.subtitle')}</div> */}
        </div>
      </div>

      <div className="flt-card" style={{ marginBottom: 14 }}>
        <div className="flt-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div className="flt-f">
            <div className="flt-l">{t('dashboards.reportingPeriod')}</div>
            <select className={fltSelClass(period)} value={period} onChange={(e) => setPeriod(e.target.value)} aria-label={t('dashboards.reportingPeriod')}>
              <option value="7d">{t('dashboards.last7Days')}</option>
              <option value="30d">{t('dashboards.last30Days')}</option>
              <option value="90d">{t('dashboards.last90Days')}</option>
            </select>
          </div>
          <div className="flt-f" style={{ gridColumn: 'span 2' }}>
            <div className="flt-l">{t('dashboards.savedPreferences')}</div>
            <div style={{ fontSize: 12, color: 'var(--t2)', paddingTop: 8 }}>
              {t('dashboards.preferencesHint')}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 14 }}>
        {dashboardKpis.map((k, i) => (
          <div key={k.label} className="flt-card" style={{ margin: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 6 }}>{t(`dashboards.${KPI_LABEL_KEYS[i]}`)}</div>
            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>{k.value}</div>
            <div style={{ fontSize: 12, color: 'var(--t2)', lineHeight: 1.45 }}>{t(`dashboards.${KPI_HINT_KEYS[i]}`)}</div>
          </div>
        ))}
      </div>

      <div className="flt-card" style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{t('dashboards.workloadGraphTitle')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {workflowLoad.map((w) => (
            <div key={w.workflow}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                <span style={{ color: 'var(--t2)' }}>{translateDashboardWorkflowLabel(w.workflow)}</span>
                <span style={{ fontWeight: 700 }}>{t('dashboards.activeCount', { count: w.active })}</span>
              </div>
              <div style={{ height: 8, borderRadius: 999, background: 'var(--bl)', overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${(w.active / maxActive) * 100}%`,
                    height: '100%',
                    background: 'var(--sb)',
                    borderRadius: 999,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <DataTable title={t('dashboards.workloadTableTitle')} columns={loadColumns} rows={workflowLoad} rowKey={(r) => r.workflow} defaultRowsPerPage={5} />
    </>
  );
}
